import React, { useState, useEffect } from 'react';
import { InputProps } from '../types';

const Input = ({
    label,
    type = "text",
    placeholder,
    value,
    onChange,
    name,
    required = false,
    theme = 'light'
}: InputProps & { theme?: 'light' | 'dark' }) => {
    const [error, setError] = useState<string>('');
    const [touched, setTouched] = useState(false);

    // Email validation
    const validateEmail = (email: string) => {
        if (!email) return '';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            return 'Please enter a valid email address';
        }
        return '';
    };

    // Phone number validation
    const validatePhone = (phone: string) => {
        if (!phone) return '';

        // Remove all non-digit characters for validation
        const cleanPhone = phone.replace(/\D/g, '');

        // Check if it's a valid phone number (10 digits for most countries)
        // You can adjust this regex based on your requirements
        if (!/^\d{10,15}$/.test(cleanPhone)) {
            return 'Please enter a valid phone number (10-15 digits)';
        }
        return '';
    };

    // Handle blur for validation
    const handleBlur = () => {
        setTouched(true);

        if (type === 'email') {
            setError(validateEmail(value as string));
        } else if (type === 'tel' || name === 'phone') {
            setError(validatePhone(value as string));
        }
    };

    // Handle change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);

        if (touched) {
            if (type === 'email') {
                setError(validateEmail(e.target.value));
            } else if (type === 'tel' || name === 'phone') {
                setError(validatePhone(e.target.value));
            }
        }
    };

    // Format phone number as user types (optional)
    const formatPhoneNumber = (value: string) => {
        if (type === 'tel' || name === 'phone') {
            // Remove all non-digit characters
            const cleanValue = value.replace(/\D/g, '');

            // Format as (XXX) XXX-XXXX
            if (cleanValue.length > 0) {
                if (cleanValue.length <= 3) {
                    return `(${cleanValue}`;
                } else if (cleanValue.length <= 6) {
                    return `(${cleanValue.slice(0, 3)}) ${cleanValue.slice(3)}`;
                } else {
                    return `(${cleanValue.slice(0, 3)}) ${cleanValue.slice(3, 6)}-${cleanValue.slice(6, 10)}`;
                }
            }
        }
        return value;
    };

    // Get formatted value for display
    const getDisplayValue = () => {
        if ((type === 'tel' || name === 'phone') && value) {
            return formatPhoneNumber(value as string);
        }
        return value;
    };

    return (
        <div className="mb-5 group">
            <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 group-focus-within:text-violet-600 transition-colors ${theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {label} {required && <span className="text-fuchsia-500">*</span>}
            </label>
            <input
                type={type === 'tel' ? 'text' : type} // Use text type for tel to control formatting
                name={name}
                value={getDisplayValue()}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={type === 'tel' ? '(123) 456-7890' : placeholder}
                required={required}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 placeholder-zinc-400 transition-all outline-none ${theme === 'dark' ? 'bg-zinc-900/50 border-zinc-700 text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900'} ${error && touched ? 'border-red-500' : ''}`}
            />
            {error && touched && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
    );
};

export default Input;